import { adminService } from '@/api/services';
import type { UnwrapData } from '@/api/types';

import type {
  IFAQsResponse,
  ICreateFAQRequest,
  ICreateFAQResponse,
  IDeleteFAQRequest,
  IReorderFAQsRequest,
  IUpdateFAQRequest,
  TUpdateFAQResponse,
} from './types';

export const schoolFAQsService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить часто задаваемые вопросы школы
     */
    getSchoolFAQs: builder.query<UnwrapData<IFAQsResponse>, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/faqs`,
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolFAQs', id: schoolId }],
      transformResponse: (response: IFAQsResponse): UnwrapData<IFAQsResponse> => response.data,
    }),
    /**
     * Создать часто задаваемого вопроса школы
     */
    createSchoolFAQ: builder.mutation<ICreateFAQResponse, ICreateFAQRequest>({
      query: ({ data, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/faqs`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolFAQs', id: schoolId }],
    }),
    /**
     * Изменить часто задаваемый вопрос школы
     */
    updateSchoolFAQ: builder.mutation<TUpdateFAQResponse, IUpdateFAQRequest>({
      query: ({ data, schoolId, questionId }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/faqs/${questionId}`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolFAQs', id: schoolId }],
    }),
    /**
     * Удалить часто задаваемый вопрос школы
     */
    deleteSchoolFAQ: builder.mutation<void, IDeleteFAQRequest>({
      query: ({ schoolId, questionId }) => ({
        method: 'DELETE',
        url: `/schools/${schoolId}/faqs/${questionId}`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolFAQs', id: schoolId }],
    }),
    /**
     * Изменить порядок часто задаваемых вопросов школы
     */
    reorderSchoolFAQs: builder.mutation<IFAQsResponse, IReorderFAQsRequest>({
      query: ({ schoolId, data }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/faqs/reorder`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolFAQs', id: schoolId }],
    }),
  }),
});

export const {
  useGetSchoolFAQsQuery,
  useCreateSchoolFAQMutation,
  useUpdateSchoolFAQMutation,
  useDeleteSchoolFAQMutation,
  useReorderSchoolFAQsMutation,
} = schoolFAQsService;
